import { useContext, useEffect, useState } from "react";
import { PageHeader } from "../../../components/page-header/page-header";
import {
  TFilterModel,
  THolidayType,
  TResponseModel,
} from "../../../types/config";
import {
  Button,
  Drawer,
  Input,
  Space,
  Table,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import CustomDrawer from "../../../components/drawer";
import GenericPopover from "../../../components/pop-over";
import ActionCard from "../../../components/actionCard/actionCard";
import HolidayForm from "../../dashboard/holidays/form";
import useFetch from "../../../hooks/useFetch";
import {
  AddEditCalendarEvent,
  GetHolidayList,
} from "../../../utils/api.constant";
import Utils from "../../../utils";
import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  FilterOutlined,
  IdcardOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { UserContext } from "../../../context/user";
import { ePrivileges, eResultCode } from "../../../utils/enum";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import DateUtils from "../../../utils/date";
import ColumnManager from "../../../components/configurable-columns";
import CustomModal from "../../../components/modal";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import useToast from "../../../hooks/useToast";
import { dateOnlyFormat, defaultFilterParams } from "../../../utils/constants";

export default function HolidayList() {
  const context: any = useContext(UserContext);
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<THolidayType[]>([]);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);

  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState<THolidayType | null>(
    null
  );
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const { onShowToast } = useToast();
  const initialColumns: any[] = [
    {
      title: "Date",
      dataIndex: "startDate",
      key: "startDate",
      width: "20%",
      filterSearch: true,
      sorter: (a: THolidayType, b: THolidayType) =>
        a.startDate?.localeCompare(b?.startDate),
      render: (startDate: string) =>
        DateUtils.format(startDate, dateOnlyFormat),
      onFilter: (value: string | number | boolean, record: THolidayType) =>
      record.startDate === value,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: "30%",
      filterSearch: true,
      sorter: (a: THolidayType, b: THolidayType) =>
        a.title?.localeCompare(b?.title),
      onFilter: (value: string | number | boolean, record: THolidayType) =>
        record.title?.includes(value as string),
    },
    // {
    //   title: "End Date",
    //   dataIndex: "endDate",
    //   key: "endDate",
    //   width: "15%",
    //   filterSearch: true,
    //   sorter: (a: THolidayType, b: THolidayType) =>
    //     a.endDate?.localeCompare(b?.endDate),
    // },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "43%",
      filterSearch: true,
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: "7%",
      render: (item: THolidayType) => (
        <>
          <GenericPopover
            placement="left"
            content={
              <div className="standardActionBox">
                {/* Edit Row with CustomDrawer trigger */}
                {Utils.isUserHasAccess(
                  context.user?.privilegeList,
                  ePrivileges.EDITCALENTRY
                ) ? (
                  <CustomDrawer
                    size="default"
                    trigger={
                      <ActionCard text="Edit">
                        <EditOutlined />
                      </ActionCard>
                    }
                    component={HolidayForm} // Passing ServicePartner component
                    componentProps={{
                      id: item.id,
                      holiday: item, // Pass necessary props for ServicePartner
                      onRefreshList: () => {
                        fetchData();
                      },
                    }}
                    title="Config Product Form"
                  />
                ) : null}

                {/* TODO ::: Add privilege check on delete action */}
                <ActionCard
                  text="Delete"
                  onClick={() => {
                    setIsModalVisible(true);
                    setSelectedHoliday(item);
                  }}
                >
                  <DeleteOutlined />
                </ActionCard>
              </div>
            }
            icon={
              <MoreOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
            }
          />
        </>
      ),
    },
  ];

  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);

  useEffect(() => {
    fetchData();
  }, []);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter((item) => {
    const searchMatch = Object.values(item).some((val) =>
      val?.toString().toLowerCase().includes(searchText.toLowerCase())
    );

    if (!searchMatch) return false;

    const startDateMatch =
      !activeFilters.startDate ||
      !activeFilters.startDate.length ||
      activeFilters.startDate.includes(item.startDate);

    const titleMatch =
      !activeFilters.title ||
      !activeFilters.title.length ||
      activeFilters.title.includes(item.title);

    const descriptionMatch =
      !activeFilters.description ||
      !activeFilters.description.length ||
      activeFilters.description.includes(item.description);

    return searchMatch && startDateMatch && titleMatch && descriptionMatch;
  });

  const updateColumnFilters = (dataset: THolidayType[]) => {
    const startDateFilters = Array.from(
      new Set(dataset.map((item) => item.startDate))
    ).map((date) => ({
      text: DateUtils.format(date, dateOnlyFormat),
      value: date,
    }));

    const titleFilters = Array.from(
      new Set(dataset.filter((item) => item.title).map((item) => item.title))
    ).map((title) => ({ text: title, value: title }));

    const descriptionFilters = Array.from(
      new Set(
        dataset
          .filter((item) => item.description)
          .map((item) => item.description)
      )
    ).map((description) => ({
      text: description,
      value: description,
    }));

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "startDate") {
          return { ...col, filters: startDateFilters };
        }
        if (col.key === "title") {
          return { ...col, filters: titleFilters };
        }
        if (col.key === "description") {
          return { ...col, filters: descriptionFilters };
        }
        return col;
      })
    );
  };

  useEffect(() => {
    updateColumnFilters(filteredData);
  }, [activeFilters, data]);

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  // useEffect(() => {
  //   const savedColumns = ColumnConfigManager.loadConfig(
  //     eColumnConfigKeys.ConfigProductList,
  //     initialColumns
  //   );
  //   setColumns(savedColumns);
  //   setDraftColumns(savedColumns);
  // }, []);

  const handleSaveColumns = () => {
    // ColumnConfigManager.saveConfig(
    //   eColumnConfigKeys.ConfigProductList,
    //   draftColumns
    // );
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns?.filter((col) => !col.hidden);

  const exportHeaders = columns
    .filter(
      (column) =>
        column.key && (column?.title as string)?.toLowerCase() !== "action"
    )
    .map((column) => ({
      label: column.title,
      key: column.key,
    }));

  const exportData = data.map((user) => ({
    ...user,
  }));

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
  };

  const handleCancel = () => {
    setSelectedHoliday(null);
    setIsModalVisible(false);
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const payload = {
        data: {
          pageSize: -1,
          currentPage: 1,
        },
      };
      const response: TResponseModel = await post(GetHolidayList, payload);
      const { data, dataResponse, filterModel } = response;
      const { returnCode } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        setData(data);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);
        updateColumnFilters(filteredData);
      }
    } catch (error) {
      console.error("Error fetching holidays:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOk = async () => {
    try {
      const payload = {
        data: {
          id: selectedHoliday?.id,
          isDeleted: 1,
        },
      };

      const response: TResponseModel = await post(
        AddEditCalendarEvent,
        payload
      );
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        setIsModalVisible(false);
        fetchData();
      } else {
        console.error("Unexpected API response format:", response);
      }
    } catch (error) {
      console.error("Error deleting holiday:", error);
    }
  };

  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardOutlined />}
        title={`${filterParams?.totalRows} Total Holidays`}
        size="large"
      >
        <Space>
          {Utils.isUserHasAccess(
            context.user?.privilegeList,
            ePrivileges.ADDCALENTRY
          ) ? (
            <CustomDrawer
              size="default"
              trigger={
                <Button disabled={isLoading} type="primary">
                  Add
                </Button>
              }
              component={HolidayForm} // Passing Service Partner Form component
              componentProps={{
                id: 0, // Pass necessary props for ServicePartnerForm
                onRefreshList: () => {
                  fetchData();
                },
              }}
              title="Service Partner Form"
            />
          ) : null}
          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`ProjectCalendar_${DateUtils.format(new Date())}.csv`}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          sticky
          scroll={{
            y: "calc(100vh - 310px)", // Adjust height dynamically
          }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>

      {/* Modal to ask the confirmation before proceeding with delete operation */}
      <CustomModal
        title="Confirm"
        isOpen={isModalVisible} // Ensure selectedUserId is valid
        okText="Yes"
        cusHeader={true}
        cancelText="No"
        onOk={async () => await handleOk()}
        onCancel={() => handleCancel()}
      >
        <p>
          You are about to delete <b>{selectedHoliday?.title}</b>
        </p>
      </CustomModal>
    </div>
  );
}
